import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TmLanguageSelectComponent } from '@teammood/shared/ui/language-select';
import { TmOutOfOfficeComponent } from '@teammood/shared/ui/out-of-office';
import { TmSlideToggleComponent } from '@teammood/shared/ui/slide-toggle';
import { SnackBarService } from '@teammood/shared/ui/snack-bar';
import { TmTagsInputComponent } from '@teammood/shared/ui/tags-input';
import { TmTimepickerComponent } from '@teammood/shared/ui/timepicker';
import { TmTimeZonePickerComponent } from '@teammood/shared/ui/timezone-picker';
import { TmWorkingDaysComponent } from '@teammood/shared/ui/working-days';
import { TmValidators } from '@teammood/shared/util/form';
import { User, UserService } from '@teammood/shared/util/user';
import { debounceTime, distinctUntilChanged, filter, merge, tap } from 'rxjs';
import { Team } from '../../team.type';
import { TeamMemberService } from '../team-member.service';
import { TeamMember } from '../team-member.type';
import { LoggerService } from "@teammood/shared/util/logger";
import { MatTabsModule } from '@angular/material/tabs';
import { TeamMemberRolesComponent } from '../team-member-roles/team-member-roles.component';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'tm-team-member-form',
    templateUrl: './team-member-form.component.html',
    styleUrls: ['./team-member-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        FormsModule,
        MatTabsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatTooltipModule,
        ReactiveFormsModule,
        TmLanguageSelectComponent,
        TmSlideToggleComponent,
        TmOutOfOfficeComponent,
        TmTagsInputComponent,
        TmTimepickerComponent,
        TmTimeZonePickerComponent,
        TmWorkingDaysComponent,
        TranslateModule,
        TeamMemberRolesComponent
    ]
})
export class TmTeamMemberFormComponent implements OnInit {
  private _team: Team;
  private _member: Partial<TeamMember>;
  protected allTags: string[] = [];
  protected currentUser?: User;
  protected disableNotificationTab: boolean;


  form: FormGroup = new FormGroup({
    email: new FormControl('', [TmValidators.required, TmValidators.email]),
    firstname: new FormControl('', [TmValidators.required]),
    lastname: new FormControl('', [TmValidators.required]),
    active: new FormControl(true),
    roles: new FormControl([]),
    tags: new FormControl([]),
    notificationTime: new FormGroup({
      timeZone: new FormControl(''),
      time: new FormControl(''),
    }),
    workingDays: new FormControl(),
    language: new FormControl(''),
    outOfOffice: new FormControl(),
    emailEnabled: new FormControl(true),
    slackEnabled: new FormControl(false),
    msTeamsEnabled: new FormControl(false),
  });

  get email() {
    return this.form.get('email');
  }

  get firstname() {
    return this.form.get('firstname');
  }

  get lastname() {
    return this.form.get('lastname');
  }

  get active() {
    return this.form.get('active');
  }

  @Input()
  get member() {
    return this._member;
  }

  set member(value) {
    this._member = value;
    if (value) {
      this.form.reset(value, { emitEvent: false });
      if (this._member.teams?.length > 1) {
        this.form.get('email').disable({ emitEvent: false });
      } else {
        this.form.get('email').enable({ emitEvent: false });
      }
      this._toggleOwnEditing();
    }
  }

  @Input({ required: true })
  get team() {
    return this._team;
  }

  set team(value) {
    this._team = value;
    this._updateReferenceData(value.id);
  }

  get teamId() {
    return this.team.id;
  }

  get memberId() {
    return this.member?.id;
  }

  get isUpdate() {
    return !!this.member?.id;
  }

  get hasMultipleTeams() {
    return this.member?.teams.length > 1;
  }

  @Output()
  onSubmit = new EventEmitter();

  @Output()
  onUpdate = new EventEmitter();

  constructor(
    private _snackBarService: SnackBarService,
    private _teamMemberService: TeamMemberService,
    private _translateService: TranslateService,
    private _userService: UserService,
    private _loggerService: LoggerService,
  ) {

    const skipControls = ['active', 'role', 'tags', 'emailEnabled', 'slackEnabled', 'msTeamsEnabled', 'webexEnabled'];
    //Listen for all controls that requires to save changes
    const controls = Object.keys(this.form.controls)
      .filter(key => !skipControls.some(skipped => skipped === key))
      .map(k => this.form.controls[k]);

    merge(
      ...controls.map(control => control.valueChanges.pipe(debounceTime(500), distinctUntilChanged()))
    ).pipe(
      filter(() => this.isUpdate && this.form.valid),
      tap(() => this.saveMemberChanges()),
      takeUntilDestroyed()
    ).subscribe()
  }

  async ngOnInit() {
    this.currentUser = await this._userService.get();
    this._toggleOwnEditing();
    this.disableNotificationTab = this.member.roles.indexOf('observer') !== -1;
  }

  private async _updateReferenceData(teamId: string) {
    this.allTags = await this._teamMemberService.getTags(teamId)
  }

  private _toggleOwnEditing() {
    //A member can not disable himself
    if (this.isUpdate && this._member && this.currentUser && this._member.id === this.currentUser.id) {
      this.form.get('active').disable({ emitEvent: false });
      this.form.get('roles').disable({ emitEvent: false });
    } else {
      this.form.get('active').enable({ emitEvent: false });
      this.form.get('roles').enable({ emitEvent: false });
    }
  }

  async saveMemberChanges() {
    if (this.isUpdate && this.form.valid) {
      const formValue = this.form.getRawValue();
      const updatedMember = { ...this.member, ...formValue };
      const result =
          await this._teamMemberService
              .update(this.team.id, updatedMember.id, updatedMember)
              .then(result => {
                if (!result.error) {
                  this._snackBarService.success(this._translateService.instant(result.message));
                } else {
                  this._snackBarService.error(result.message);
                }
              })
              .catch(error => {
                this._loggerService.error(error)
              });

      this.onUpdate.next(result);
    }
  }

  async toggle_status(event) {
    const newVal = event.value;
    this.form.get('active').setValue(newVal, { emitEvent: false });
    await this._tryUpdate(() => this._teamMemberService.toggle(this.teamId, this.member.id))
  }

  async updateRole(newRoles: string[]) {
    if (this.isUpdate) {
      try {
        const result = await this._teamMemberService.update_roles(this.teamId, this.member.id, newRoles);
        this.disableNotificationTab = newRoles.includes('observer');
        this.onUpdate.next(result);
        this._snackBarService.success('changeSaved')
        if(this._member.id === this.currentUser.id){
          this.currentUser = await this._userService.get();
          this._userService.set(this.currentUser);
        }
      } catch (error) {
        if (error.status === 406) {
          // Reset role to previous value
          this.form.patchValue({ roles: this.member.roles }, { emitEvent: false });
          this._snackBarService.error(error.error);
        } else {
          throw error;
        }
      }
    } else {
      this._member.roles = newRoles;
      this.form.get('roles').setValue(newRoles);
    }
  }

  async add_tag(tag: string) {
    await this._tryUpdate(() => this._teamMemberService.addTag(this.teamId, this.member.id, tag))
  }

  async remove_tag(tag: string) {
    await this._tryUpdate(() => this._teamMemberService.removeTag(this.teamId, this.member.id, tag))
  }

  async toggle_channel(channel: string) {
    await this._tryUpdate(() => this._teamMemberService.toggleChannel(this.teamId, this.member.id, channel));
  }

  submit() {
    if (this.form.valid) {
      this.onSubmit.emit(this.form.getRawValue());
    }
  }

  fillFirstAndLastnameFromEmail() {
    if (this.email.value && !this.isUpdate) {
      const emailParts = this.email.value.split("@");
      const userName = emailParts.length === 2 ? emailParts[0] : null;

      if (userName !== null) {

        const nameParts = userName.split(".");
        const firstName = nameParts.length === 2 ? upperCaseFirstLetter(nameParts[0]) : null;
        const lastName = nameParts.length === 2 ? upperCaseFirstLetter(nameParts[1]) : null;

        if (firstName && (!this.firstname.value || this.firstname.value === "")) {
          this.firstname.setValue(firstName, { emitEvent: false });
        }
        if (lastName && (!this.lastname.value || this.lastname.value === "")) {
          this.lastname.setValue(lastName, { emitEvent: false });
        }
      }
    }
  }

  private async _tryUpdate(updateFn: () => Promise<TeamMember>) {
    if (this.isUpdate) {
      const result = await updateFn();
      this.onUpdate.next(result);
      this._snackBarService.success(this._translateService.instant('changeSaved'))
    }
  }
}

const upperCaseFirstLetter = (s: string) => {
  return s.charAt(0).toUpperCase() + s.slice(1);
}